import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - scrollama doesn't have perfect TypeScript support
import scrollama from 'scrollama';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  BookOpen, QrCode, Smartphone, BarChart3, Users, Globe, 
  Camera, TrendingUp, Shield, Sparkles, Menu, X, Play, PlayCircle 
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { cn } from '../lib/utils';
// Pricing temporarily disabled
import { PDFProductionWrapper } from '../components/qr/PDFProductionWrapper';
import ImpressumContent from '../components/legal/ImpressumContent';

gsap.registerPlugin(ScrollTrigger);

interface ScrollStep {
  id: string;
  title: string;
  content: string;
  graphic?: string;
  backgroundVideo?: string;
  animation?: 'fade' | 'zoom' | 'parallax' | 'slide';
  layout?: 'center' | 'left' | 'right' | 'overlay' | 'split';
  theme?: 'corporate' | 'dark' | 'light' | 'navy' | 'steel';
}

const LandingPageNew = () => {
  const scrollerRef = useRef<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isOnHero, setIsOnHero] = useState(true);
  const [hoveredDemo, setHoveredDemo] = useState<string | null>('spotify');
  const [lastViewedDemo, setLastViewedDemo] = useState<string>('spotify');
  const [demosViewed, setDemosViewed] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);
  const lastScrollTime = useRef(0);
  const isScrollingProgrammatically = useRef(false);
  const navigate = useNavigate();

  const handlePlanSelect = (planId: string) => {
    sessionStorage.setItem('selectedPlan', planId);
    navigate('/qr/login');
  };

  const storySteps: ScrollStep[] = [
    {
      id: 'intro',
      title: 'Vision',
      content: `
        <p class="text-xl leading-relaxed mb-6 text-gray-700">
          Erweitern Sie Ihre gedruckten Bücher mit interaktiven QR-Codes. Verbinden Sie Ihre Leser mit Spotify-Playlists, Videos, Leseproben und mehr.
        </p>
      `,
      graphic: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&h=1080&fit=crop',
      animation: 'zoom' as const,
      layout: 'center' as const,
      theme: 'corporate' as const
    },
    {
      id: 'challenge',
      title: 'Was ist das Problem für Verlage heute?',
      content: `
        <div class="max-w-4xl mx-auto">
          <ul class="text-xl space-y-4 text-gray-100 font-medium mb-8">
            <li class="flex items-start">
              <span class="text-red-400 mr-3 text-2xl">✗</span>
              <span>Anzeigenkunden fordern Daten – Print liefert keine Zahlen</span>
            </li>
            <li class="flex items-start">
              <span class="text-red-400 mr-3 text-2xl">✗</span>
              <span>Nach dem Druck bleiben Inhalte unveränderbar</span>
            </li>
            <li class="flex items-start">
              <span class="text-red-400 mr-3 text-2xl">✗</span>
              <span>Autoren haben keine Bindung zu ihren Lesern</span>
            </li>
            <li class="flex items-start">
              <span class="text-red-400 mr-3 text-2xl">✗</span>
              <span>Kampagnen in Print sind blind, teuer, nicht messbar</span>
            </li>
          </ul>
          <div class="border-t border-gray-600 pt-8 mt-8">
            <p class="text-3xl font-bold text-white text-center">
              Mit uns endet Blindflug im Print.
            </p>
          </div>
        </div>
      `,
      graphic: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=1920&h=1080&fit=crop',
      animation: 'slide' as const,
      layout: 'left' as const,
      theme: 'navy' as const
    },
    {
      id: 'solution',
      title: 'Lösung',
      content: `
        <div class="bg-blue-50 p-6 md:p-8 rounded-lg mb-6 md:mb-8 w-full">
          <p class="text-lg md:text-xl text-blue-800 font-medium text-center">
            «Ein Scan genügt – und Ihre Leser tauchen in eine neue Dimension ein.»
          </p>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div>
            <ul class="text-base md:text-lg space-y-1 md:space-y-2">
              <li>• Spotify-Playlists für Buchsoundtracks</li>
              <li>• YouTube-Videos für Buchtrailer</li>
              <li>• Digitale Leseproben mit E-Mail-Erfassung</li>
              <li>• Multi-Shop-Links für alle Verkaufskanäle</li>
              <li>• Community-Pages mit Kommentarfunktion</li>
            </ul>
          </div>
          <div>
            <ul class="text-base md:text-lg space-y-1 md:space-y-2">
              <li>• Autorenprofile mit Social Media Links</li>
              <li>• Bonusmaterial & Making-of Content</li>
              <li>• Newsletter-Anmeldung direkt vom Buch</li>
              <li>• Tiefgreifende Statistiken</li>
              <li>• Weitere Lösungen erstellen wir Ihnen individuell</li>
            </ul>
          </div>
        </div>
      `,
      graphic: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=1920&h=1080&fit=crop',
      animation: 'parallax' as const,
      layout: 'center' as const,
      theme: 'steel' as const
    },
    {
      id: 'video',
      title: 'Tutorial Video',
      content: ``,
      graphic: 'video-embed',
      animation: 'fade' as const,
      layout: 'center' as const,
      theme: 'dark' as const
    },
    {
      id: 'implementation',
      title: 'So funktioniert\'s',
      content: `
        <div class="w-full flex items-center justify-center">
          <div class="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4">
          <div class="backdrop-blur-xl border border-white/20 py-6 px-6 rounded-lg flex flex-col items-center text-center w-[180px] h-[170px] justify-center" style="background-color: rgba(99, 70, 59, 0.6)">
            <span class="text-3xl md:text-4xl font-bold text-white mb-2">1</span>
            <strong class="text-sm md:text-base text-white">QR-Code erstellen</strong>
            <span class="text-xs md:text-sm text-white/80">Wählen Sie den Inhaltstyp</span>
          </div>
          <svg class="hidden md:block w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          <div class="backdrop-blur-xl border border-white/20 py-6 px-6 rounded-lg flex flex-col items-center text-center w-[180px] h-[170px] justify-center" style="background-color: rgba(99, 70, 59, 0.6)">
            <span class="text-3xl md:text-4xl font-bold text-white mb-2">2</span>
            <strong class="text-sm md:text-base text-white">Inhalt verknüpfen</strong>
            <span class="text-xs md:text-sm text-white/80">Links und Dateien hinzufügen</span>
          </div>
          <svg class="hidden md:block w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          <div class="backdrop-blur-xl border border-white/20 py-6 px-6 rounded-lg flex flex-col items-center text-center w-[180px] h-[170px] justify-center" style="background-color: rgba(99, 70, 59, 0.6)">
            <span class="text-3xl md:text-4xl font-bold text-white mb-2">3</span>
            <strong class="text-sm md:text-base text-white">QR-Code designen</strong>
            <span class="text-xs md:text-sm text-white/80">Farbe und Form anpassen</span>
          </div>
          <svg class="hidden md:block w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          <div class="backdrop-blur-xl border border-white/20 py-6 px-6 rounded-lg flex flex-col items-center text-center w-[180px] h-[170px] justify-center" style="background-color: rgba(99, 70, 59, 0.6)">
            <span class="text-3xl md:text-4xl font-bold text-white mb-2">4</span>
            <strong class="text-sm md:text-base text-white">QR-Code drucken</strong>
            <span class="text-xs md:text-sm text-white/80">In Ihr Buch integrieren</span>
          </div>
          <svg class="hidden md:block w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
          <div class="backdrop-blur-xl border border-white/20 py-6 px-6 rounded-lg flex flex-col items-center text-center w-[180px] h-[170px] justify-center" style="background-color: rgba(99, 70, 59, 0.6)">
            <span class="text-3xl md:text-4xl font-bold text-white mb-2">5</span>
            <strong class="text-sm md:text-base text-white">Erfolg messen</strong>
            <span class="text-xs md:text-sm text-white/80">Scans verfolgen</span>
          </div>
          </div>
        </div>
      `,
      graphic: 'custom-implementation',
      animation: 'fade' as const,
      layout: 'split' as const,
      theme: 'dark' as const
    },
    {
      id: 'demos',
      title: 'Live Demos',
      content: `demo-content`, // Placeholder - will be rendered separately
      graphic: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1920&h=1080&fit=crop',
      animation: 'zoom' as const,
      layout: 'center' as const,
      theme: 'dark' as const
    },
    {
      id: 'verlage',
      title: 'Enterprise-ready für moderne Verlage',
      content: `
        <div class="text-center mb-6">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Enterprise-ready für moderne Verlage.
          </h2>
        </div>

        <div class="bg-white rounded-xl shadow-xl p-6 md:p-8 max-w-5xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Analytics & Reporting</h3>
                <p class="text-xs text-gray-600">Echtzeitdaten für Anzeigenkunden</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">White-Label & API</h3>
                <p class="text-xs text-gray-600">Nahtlose Integration</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Teams & Rechte</h3>
                <p class="text-xs text-gray-600">Multi-User Verwaltung</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">A/B-Testing</h3>
                <p class="text-xs text-gray-600">Kampagnen optimieren</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Print-ready</h3>
                <p class="text-xs text-gray-600">PNG, SVG, EPS</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Skalierbar</h3>
                <p class="text-xs text-gray-600">10 bis 10.000 Titel</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-4 text-white">
            <h3 class="font-bold text-lg mb-1">🚀 Pilot-Programm 2025</h3>
            <p class="mb-3 opacity-90 text-sm">Begrenzte Plätze – persönliche Betreuung durch Gründerteam.</p>
            <a
              href="mailto:info@beyondpages.io?subject=Enterprise%20Pilot-Programm%202025&body=Hallo%20BeyondPages-Team%2C%0A%0Aich%20interessiere%20mich%20f%C3%BCr%20das%20Enterprise%20Pilot-Programm.%0A%0AUnser%20Verlag%3A%0ATitelanzahl%3A%0A%0ABitte%20kontaktieren%20Sie%20mich%20f%C3%BCr%20weitere%20Details.%0A%0AViele%20Gr%C3%BC%C3%9Fe"
              class="inline-block px-6 py-2 bg-white hover:bg-gray-100 text-blue-600 font-semibold rounded transition-colors shadow-lg text-sm"
            >
              Pilot-Programm anfragen →
            </a>
          </div>
        </div>
      `,
      graphic: 'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=1920&h=1080&fit=crop',
      animation: 'fade' as const,
      layout: 'center' as const,
      theme: 'corporate' as const
    },
    {
      id: 'autoren',
      title: 'Für Autor:innen',
      content: `
        <div class="text-center mb-6">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            ✍️ Für Selfpublisher & Verlagsautor:innen
          </h2>
          <p class="text-lg text-gray-600">
            In 2 Minuten einsatzbereit – ohne Technik-Know-how
          </p>
        </div>

        <div class="bg-white rounded-xl shadow-xl p-6 md:p-8 max-w-5xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Spotify-Musik</h3>
                <p class="text-xs text-gray-600">Soundtrack zum Buch</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">YouTube-Videos</h3>
                <p class="text-xs text-gray-600">Behind-the-Scenes</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">E-Mail-Gate</h3>
                <p class="text-xs text-gray-600">Mailingliste aufbauen</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Multi-Shop</h3>
                <p class="text-xs text-gray-600">Amazon, Thalia & Co.</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">Anpassbar</h3>
                <p class="text-xs text-gray-600">Dein Style & Design</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <div class="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 text-sm">29€/Monat</h3>
                <p class="text-xs text-gray-600">Fair & transparent</p>
              </div>
            </div>
          </div>

          <div class="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
            <h3 class="font-bold text-purple-900 text-lg mb-1">🎯 Jetzt kostenlos testen</h3>
            <p class="text-purple-800 mb-3 text-sm">Keine Kreditkarte · 7 Tage Testphase</p>
            <a
              href="/qr/login"
              class="inline-block px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded transition-colors shadow-lg text-sm"
            >
              Kostenlos starten →
            </a>
          </div>
        </div>
      `,
      graphic: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1920&h=1080&fit=crop',
      animation: 'fade' as const,
      layout: 'center' as const,
      theme: 'corporate' as const
    },
    {
      id: 'trust',
      title: 'Enterprise-Standards. Ohne Kompromisse.',
      content: `
        <div class="text-center mb-6">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900">
            Enterprise-Standards. Ohne Kompromisse.
          </h2>
        </div>

        <div class="max-w-5xl mx-auto">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div class="text-center">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
                <span class="text-3xl">🛡️</span>
              </div>
              <h3 class="font-bold text-gray-900 text-sm mb-1">DSGVO-konform</h3>
              <p class="text-xs text-gray-600">EU-Hosting Deutschland</p>
            </div>

            <div class="text-center">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
                <span class="text-3xl">🔒</span>
              </div>
              <h3 class="font-bold text-gray-900 text-sm mb-1">SSL-Verschlüsselung</h3>
              <p class="text-xs text-gray-600">Ende-zu-Ende gesichert</p>
            </div>

            <div class="text-center">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-3">
                <span class="text-3xl">📜</span>
              </div>
              <h3 class="font-bold text-gray-900 text-sm mb-1">AV-Vertrag</h3>
              <p class="text-xs text-gray-600">Rechtssicher dokumentiert</p>
            </div>

            <div class="text-center">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-3">
                <span class="text-3xl">⚙️</span>
              </div>
              <h3 class="font-bold text-gray-900 text-sm mb-1">99.9% Uptime</h3>
              <p class="text-xs text-gray-600">Höchste Verfügbarkeit</p>
            </div>
          </div>

          <div class="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
            <p class="text-lg text-gray-800 text-center font-medium">
              BeyondPages erfüllt alle Anforderungen professioneller Verlags-IT –<br class="hidden md:block" />
              von Datenschutz bis Stabilität.
            </p>
          </div>
        </div>
      `,
      graphic: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1920&h=1080&fit=crop',
      animation: 'fade' as const,
      layout: 'center' as const,
      theme: 'light' as const
    },
  ];

  // Temporarily hide the "So funktioniert's" section
  const visibleSteps = storySteps.filter(step => step.id !== 'implementation');

  const getThemeClasses = (theme: string) => {
    switch (theme) {
      case 'corporate': return 'bg-gradient-to-br from-slate-50 via-gray-100 to-blue-50';
      case 'dark': return 'bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900';
      case 'light': return 'bg-gradient-to-br from-white via-gray-50 to-slate-100';
      case 'navy': return 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900';
      case 'steel': return 'bg-gradient-to-br from-gray-800 via-slate-700 to-blue-800';
      default: return 'bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900';
    }
  };

  const scrollToSection = (index: number) => {
    const totalSections = visibleSteps.length + 2; // Hero + story steps + footer
    if (index < 0 || index >= totalSections) return;
    
    isScrollingProgrammatically.current = true;
    
    // Special handling for sections
    let targetY;
    if (index === totalSections - 1) {
      // Scroll to footer (last section)
      targetY = document.body.scrollHeight - window.innerHeight;
    } else {
      targetY = index * window.innerHeight;
    }
    
    window.scrollTo({
      top: targetY,
      behavior: 'smooth'
    });
    
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 500); // Reduced to 500ms for quicker response
  };

  const handleWheel = (event: WheelEvent) => {
    if (isScrollingProgrammatically.current) {
      event.preventDefault();
      return;
    }

    const now = Date.now();
    if (now - lastScrollTime.current < 300) { // Reduced to 300ms for faster scrolling
      event.preventDefault();
      return;
    }

    lastScrollTime.current = now;
    event.preventDefault();

    const delta = event.deltaY;
    const currentSection = Math.round(window.scrollY / window.innerHeight);
    const totalSections = visibleSteps.length + 2; // Hero + story steps + footer
    
    if (delta > 0) {
      const nextSection = Math.min(currentSection + 1, totalSections - 1);
      scrollToSection(nextSection);
    } else {
      const prevSection = Math.max(currentSection - 1, 0);
      scrollToSection(prevSection);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    document.body.style.scrollSnapType = 'y mandatory';
    document.body.style.overflowY = 'scroll';

    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
      document.body.style.scrollSnapType = '';
      document.body.style.overflowY = '';
    };
  }, [visibleSteps.length]);


  // Check if we're on the hero section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const currentSection = Math.round(scrollPosition / windowHeight);
      setIsOnHero(currentSection === 0);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    scrollerRef.current = scrollama();
    
    scrollerRef.current
      .setup({
        step: '.story-step',
        offset: 0.5,
        progress: true,
        debug: false,
      })
      .onStepEnter(({ element, index }: { element: HTMLElement; index: number }) => {
        setActiveStep(Math.min(index, visibleSteps.length));
        
        const stepData = visibleSteps[index];
        if (!stepData) {
          return;
        }
        
        // Reset video when navigating away and back
        if (stepData.graphic !== 'video-embed' && videoPlaying) {
          setVideoPlaying(false);
        }
        
        // Mark demos as viewed when entering the demos section
        if (stepData.id === 'demos' && !demosViewed) {
          setDemosViewed(true);
        }
        
        const graphic = element.querySelector('.step-graphic');
        const text = element.querySelector('.step-text');
        
        switch (stepData.animation) {
          case 'zoom':
            if (graphic) gsap.fromTo(graphic, 
              { scale: 1.08, opacity: 0.3 },
              { scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" }
            );
            break;
            
          case 'slide':
            if (graphic) gsap.fromTo(graphic, 
              { x: 50, opacity: 0.4 },
              { x: 0, opacity: 1, duration: 0.3, ease: "power3.out" }
            );
            break;
            
          case 'parallax':
            if (graphic) gsap.fromTo(graphic,
              { yPercent: 10, scale: 1.05, opacity: 0.5 },
              { yPercent: 0, scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" }
            );
            break;
            
          default:
            if (graphic) gsap.fromTo(graphic,
              { opacity: 0.2, y: 20 },
              { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }
            );
        }
        
        if (text) {
          gsap.fromTo(text,
            { opacity: 0, y: 15 },
            { opacity: 1, y: 0, duration: 0.3, delay: 0, ease: "power2.out" }
          );
        }
      })
      .onStepExit(({ element }: { element: HTMLElement }) => {
        const graphic = element.querySelector('.step-graphic');
        if (graphic) {
          gsap.to(graphic, { opacity: 0.5, duration: 0.2, ease: "power2.out" });
        }
      })
      .onStepProgress(({ element, progress }: { element: HTMLElement; progress: number }) => {
        const graphic = element.querySelector('.step-graphic') as HTMLElement;
        
        if (graphic) {
          gsap.to(graphic, {
            yPercent: (1 - progress) * 2,
            scale: 0.995 + (progress * 0.005),
            duration: 0.3,
            ease: "power2.out"
          });
        }
      });

    return () => {
      scrollerRef.current?.destroy();
      ScrollTrigger.getAll().forEach((trigger: any) => trigger.kill());
    };
  }, [visibleSteps]);

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-0.5 text-white">
              <Logo size={32} />
              <span className="font-bold text-xl">BeyondPages</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <button 
                onClick={() => navigate('/qr/login')}
                className="text-white/80 hover:text-white transition-colors"
              >
                Anmelden
              </button>
            </nav>
            
            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-2">
              <button 
                onClick={() => navigate('/qr/login')}
                className="block w-full text-left text-white/80 hover:text-white py-2"
              >
                Anmelden
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="scroll-section h-screen relative overflow-hidden flex items-center justify-center bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900" style={{ scrollSnapAlign: 'start' }}>
        <div className="absolute inset-0 bg-black/30" />

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 md:-top-40 -right-20 md:-right-40 w-40 md:w-80 h-40 md:h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-20 md:-bottom-40 -left-20 md:-left-40 w-40 md:w-80 h-40 md:h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-20 md:top-40 left-20 md:left-40 w-40 md:w-80 h-40 md:h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Split Layout: Text Left, Image Right */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* Left Side: Hero Text */}
            <div className="text-center lg:text-left">
              <h1 className="font-extrabold mb-4 md:mb-6 text-white tracking-wide opacity-0 animate-[fade-in_2s_ease-out_0.5s_forwards]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-2">Print endlich</span>
                <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 text-transparent bg-clip-text animate-gradient-x">messbar, updatefähig und profitabel.</span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed font-medium opacity-0 animate-[fade-in_2s_ease-out_1s_forwards] mb-6 md:mb-8" style={{ fontFamily: 'Raleway, sans-serif' }}>
                QR-Codes für Bücher – sofort einsatzbereit, mit messbarer Wirkung dahinter.
              </p>

              {/* Two CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start opacity-0 animate-[fade-in_2s_ease-out_1.3s_forwards] mb-4">
                <a
                  href="#solution"
                  className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  Demo ansehen →
                </a>
                <a
                  href="#solution"
                  className="inline-flex items-center justify-center px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  Wie es funktioniert →
                </a>
              </div>

              {/* Target Audience Hint */}
              <p className="text-sm text-white/60 opacity-0 animate-[fade-in_2s_ease-out_1.5s_forwards] mb-6">
                Für Verlage und Publisher, die Print mit Daten verbinden wollen.
              </p>

              {/* Micro-Trust */}
              <div className="opacity-0 animate-[fade-in_2s_ease-out_1.7s_forwards]">
                <p className="text-sm text-white/70">
                  Keine Kreditkarte nötig · Testzugang in 60 Sekunden
                </p>
              </div>
            </div>

            {/* Right Side: Hero Image */}
            <div className="hidden lg:flex items-center justify-center opacity-0 animate-[fade-in_2s_ease-out_1s_forwards]">
              <img
                src="/images/20251014_2339_Elegant QR Display_simple_compose_01k7jahtpfeqytwx9vtprkqxey (1).png"
                alt="Elegante QR-Code Präsentation"
                className="w-full h-auto max-w-lg xl:max-w-xl rounded-lg shadow-2xl"
              />
            </div>

            {/* Mobile: Image Below Text */}
            <div className="lg:hidden flex items-center justify-center opacity-0 animate-[fade-in_2s_ease-out_1.5s_forwards] mt-6">
              <img
                src="/images/20251014_2339_Elegant QR Display_simple_compose_01k7jahtpfeqytwx9vtprkqxey (1).png"
                alt="Elegante QR-Code Präsentation"
                className="w-full h-auto max-w-md rounded-lg shadow-2xl"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Story Steps */}
      {visibleSteps.map((step, index) => (
        <section
          key={step.id}
          id={step.id}
          className={`scroll-section scroll-step story-step relative overflow-hidden transition-all duration-1000 ease-in-out ${
            getThemeClasses(step.theme || 'corporate')
          } ${activeStep === index ? "is-active" : ""}`}
          style={{ 
            height: '100vh',
            minHeight: '100vh',
            scrollSnapAlign: 'start'
          }}
        >
          <div className="step-graphic absolute inset-0 transition-all duration-1000 ease-in-out">
            {step.backgroundVideo ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover transition-transform duration-2000 ease-in-out"
                preload="metadata"
                style={{ minHeight: '100vh' }}
              >
                <source src={step.backgroundVideo} type="video/mp4" />
              </video>
            ) : step.graphic === 'video-embed' ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                <div className="w-full max-w-6xl px-8">
                  <div className="relative" style={{ paddingBottom: '56.25%' }}>
                    {!videoPlaying ? (
                      // Show thumbnail with play button
                      <div className="absolute inset-0 bg-black rounded-lg shadow-2xl">
                        <img 
                          src="https://img.youtube.com/vi/v1htDcwr1SA/maxresdefault.jpg" 
                          alt="Video thumbnail"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/30 rounded-lg" />
                        <button
                          onClick={() => setVideoPlaying(true)}
                          className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          style={{ zIndex: 20 }}
                        >
                          {/* YouTube's play button */}
                          <div className="bg-red-600 bg-opacity-90 rounded-2xl p-5 hover:bg-opacity-100 transition-all transform hover:scale-110">
                            <svg className="w-12 h-12" viewBox="0 0 24 24" fill="white">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </button>
                      </div>
                    ) : (
                      // Show playing video
                      <iframe
                        src="https://www.youtube.com/embed/v1htDcwr1SA?autoplay=1"
                        className="absolute inset-0 w-full h-full rounded-lg shadow-2xl"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : step.graphic === 'custom-implementation' ? (
              <div className="w-full h-full bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
                <div className="w-full max-w-[1800px] mx-auto px-4 md:px-8">
                  {/* Desktop: Centered layout, Mobile: Vertical layout below content */}
                  <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mt-[250px] md:mt-48">
                    {/* Preview Image - 30% larger */}
                    <div className="relative w-full md:w-auto flex justify-center">
                      <img 
                        src="/images/content_type_preview2.png" 
                        alt="Content Type Preview"
                        className="rounded-lg shadow-2xl w-full max-w-[676px] sm:max-w-[845px] md:w-[1352px] lg:w-[1859px] xl:w-[2028px]"
                      />
                    </div>
                    
                    {/* QR Code Image */}
                    <div className="flex flex-col items-center">
                      <img 
                        src="/images/qr-song-zum-buch.png" 
                        alt="QR Code"
                        className="rounded-lg shadow-xl w-24 h-24 md:w-32 md:h-32 lg:w-36 lg:h-36 object-contain"
                      />
                      <p className="text-white/60 text-xs md:text-sm mt-2">Ihr QR-Code</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : step.graphic ? (
              <img
                src={step.graphic}
                alt=""
                className="w-full h-full object-cover transition-transform duration-2000 ease-in-out"
                style={{ minHeight: '100vh', objectFit: 'cover' }}
              />
            ) : (
              <div className={`w-full h-full transition-all duration-1000 ease-in-out ${getThemeClasses(step.theme || 'corporate')}`} style={{ minHeight: '100vh' }} />
            )}
            
            {step.graphic !== 'video-embed' && step.graphic !== 'custom-implementation' && (
              <div className="absolute inset-0 bg-black/40 transition-opacity duration-1000 ease-in-out" />
            )}
          </div>

          <div className={`step-text ${step.id === 'implementation' ? 'relative' : 'sticky top-0'} z-10 flex transition-all duration-1000 ease-in-out py-8 md:py-0 ${
            step.id === 'implementation' ? "items-center" : "items-center"
          } ${
            step.layout === 'left' ? "justify-center md:justify-start md:pl-0" :
            step.layout === 'right' ? "justify-center md:justify-end md:pr-16" :
            step.layout === 'center' ? "justify-center" :
            step.layout === 'overlay' ? "absolute inset-0 justify-center items-center" :
            "justify-center"
          }`}
          style={{ 
            minHeight: step.id === 'implementation' ? 'auto' : '100vh',
            height: 'auto',
            pointerEvents: step.graphic === 'video-embed' ? 'none' : 'auto'
          }}
          >
            {step.content !== '' && step.id !== 'demos' && (
              <div className={`${step.id === 'challenge' ? 'max-w-[960px]' : step.id === 'implementation' ? 'max-w-fit pt-24' : 'max-w-4xl'} ${step.id === 'implementation' ? 'mx-auto' : step.layout === 'left' ? 'mx-auto md:ml-48 md:mr-auto' : 'mx-auto'} px-4 sm:px-6 md:px-8 ${
                step.layout === 'split' && step.id !== 'implementation' ? "grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl items-center" : ""
              }`}>
                {step.id === 'implementation' ? (
                  // No wrapper for implementation section - content directly
                  <div 
                    className="prose prose-lg max-w-none transition-all duration-800 ease-in-out"
                    dangerouslySetInnerHTML={{ __html: step.content }}
                  />
                ) : (
                  // Normal wrapper for other sections
                  <div className={`backdrop-blur-2xl bg-white/10 border border-white/20 p-6 sm:p-8 md:p-12 rounded-xl shadow-2xl transition-all duration-300 ease-out ${
                    step.theme === 'corporate' || step.theme === 'light' 
                      ? "bg-white/95 border-gray-200/50 text-gray-900 backdrop-blur-xl" 
                      : "bg-black/30 border-white/20 text-white backdrop-blur-2xl"
                  }`}>
                    <div 
                      className="prose prose-lg max-w-none transition-all duration-800 ease-in-out"
                      dangerouslySetInnerHTML={{ __html: step.content }}
                    />
                  </div>
                )}
              
                {step.layout === 'split' && step.id !== 'implementation' && (
                  <div className="hidden lg:flex items-center justify-center">
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl transition-all duration-800 ease-in-out ${
                      step.theme === 'corporate' || step.theme === 'light'
                        ? "bg-blue-100/80 text-blue-600"
                        : "bg-white/15 text-white"
                    }`}>
                      {index === 0 && <BookOpen className="w-12 h-12" />}
                      {index === 1 && <QrCode className="w-12 h-12" />}
                      {index === 2 && <Smartphone className="w-12 h-12" />}
                      {index === 3 && <Globe className="w-12 h-12" />}
                      {index === 4 && <Camera className="w-12 h-12" />}
                      {index === 5 && <BarChart3 className="w-12 h-12" />}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Custom Demos Section */}
            {step.id === 'demos' && (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" style={{ position: 'relative', zIndex: 20 }}>
                <div 
                  className={`bg-black/30 border border-white/20 p-6 sm:p-8 md:p-12 rounded-xl shadow-2xl ${demosViewed ? 'backdrop-blur-lg' : 'animate-blur-in'}`}
                  style={{ transition: 'box-shadow 1s, transform 1s' }}
                >
                  
                  <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
                    {/* Demo Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 flex-1 max-w-2xl">
                      {/* Spotify Demo */}
                      <div 
                        className="bg-white/15 backdrop-blur-2xl rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-105 h-[120px]"
                        onMouseEnter={() => {
                          setHoveredDemo('spotify');
                          setLastViewedDemo('spotify');
                        }}
                        onMouseLeave={() => setHoveredDemo(null)}
                      >
                        <div className="p-3 md:p-4">
                          <div className="flex items-center mb-2">
                            <div className="bg-green-500 p-2 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="font-semibold text-base text-white">Spotify Playlist</h3>
                              <p className="text-xs opacity-70 text-white">Buchsoundtrack zum Hören</p>
                            </div>
                          </div>
                          
                        </div>
                        <div className="bg-white/5 px-2 py-1 border-t border-white/10">
                          <p className="text-xs opacity-60 text-white">QR-Code führt zur Spotify-Integration</p>
                        </div>
                      </div>
                      
                      {/* Shopping Demo */}
                      <div 
                        className="bg-white/15 backdrop-blur-2xl rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-105 h-[120px]"
                        onMouseEnter={() => {
                          setHoveredDemo('shop');
                          setLastViewedDemo('shop');
                        }}
                        onMouseLeave={() => setHoveredDemo(null)}
                      >
                        <div className="p-3 md:p-4">
                          <div className="flex items-center mb-2">
                            <div className="bg-blue-500 p-2 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="font-semibold text-base text-white">Multi-Shop</h3>
                              <p className="text-xs opacity-70 text-white">Alle Händler auf einen Blick</p>
                            </div>
                          </div>
                          
                        </div>
                        <div className="bg-white/5 px-2 py-1 border-t border-white/10">
                          <p className="text-xs opacity-60 text-white">QR-Code führt zu Shopping-Links</p>
                        </div>
                      </div>
                      
                      {/* Author Profile Demo */}
                      <div 
                        className="bg-white/15 backdrop-blur-2xl rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-105 h-[120px]"
                        onMouseEnter={() => {
                          setHoveredDemo('author');
                          setLastViewedDemo('author');
                        }}
                        onMouseLeave={() => setHoveredDemo(null)}
                      >
                        <div className="p-3 md:p-4">
                          <div className="flex items-center mb-2">
                            <div className="bg-purple-500 p-2 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="font-semibold text-base text-white">Autorenprofil</h3>
                              <p className="text-xs opacity-70 text-white">Persönliche Autorenvorstellung</p>
                            </div>
                          </div>
                          
                        </div>
                        <div className="bg-white/5 px-2 py-1 border-t border-white/10">
                          <p className="text-xs opacity-60 text-white">QR-Code führt zum Autorenprofil</p>
                        </div>
                      </div>
                      
                      {/* Book Sample Demo */}
                      <div 
                        className="bg-white/15 backdrop-blur-2xl rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-105 h-[120px]"
                        onMouseEnter={() => {
                          setHoveredDemo('sample');
                          setLastViewedDemo('sample');
                        }}
                        onMouseLeave={() => setHoveredDemo(null)}
                      >
                        <div className="p-3 md:p-4">
                          <div className="flex items-center mb-2">
                            <div className="bg-orange-500 p-2 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="font-semibold text-base text-white">Leseprobe</h3>
                              <p className="text-xs opacity-70 text-white">Digitale Buchvorschau</p>
                            </div>
                          </div>
                          
                        </div>
                        <div className="bg-white/5 px-2 py-1 border-t border-white/10">
                          <p className="text-xs opacity-60 text-white">QR-Code führt zur PDF-Leseprobe</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Phone Mockup */}
                    <div className="flex-shrink-0 lg:ml-12">
                      <div className="relative">
                        {/* Phone Frame */}
                        <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl">
                          {/* Screen */}
                          <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white">
                            {/* Demo Preview */}
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              {(hoveredDemo === 'spotify' || (!hoveredDemo && lastViewedDemo === 'spotify')) && (
                                <div className="w-full h-full overflow-y-auto" style={{ background: 'linear-gradient(to bottom right, #4F46E5, #7C3AED)' }}>
                                  {/* Spotify Demo Content - Exact copy from SpotifyDemo.tsx */}
                                  <div className="min-h-full relative">
                                    {/* Header */}
                                    <div className="relative z-10">
                                      <div className="px-4 py-4">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2 text-white">
                                            <span className="font-bold text-sm">Max Mustermann GmbH</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Main Content */}
                                    <div className="relative z-10 px-3 pb-4">
                                      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-3 shadow-2xl">
                                        {/* Book Cover Section */}
                                        <div className="flex items-start gap-3 mb-3">
                                          <div className="relative flex-shrink-0">
                                            <img
                                              src="/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp"
                                              alt="Der Rabe und andere Gedichte"
                                              className="w-20 h-32 object-cover rounded-lg shadow-xl"
                                            />
                                            <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1 shadow-lg">
                                              <Sparkles className="w-2 h-2" />
                                            </div>
                                          </div>
                                          
                                          <div className="text-white flex-1">
                                            <h2 className="text-sm font-bold mb-0.5">
                                              Entdecke die Playlist
                                            </h2>
                                            <p className="text-white/80 text-xs mb-1">zu "Der Rabe und andere Gedichte"</p>
                                            <div className="flex items-center gap-1 text-white/60 text-xs">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
                                              </svg>
                                              <span>Begleitende Playlist</span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Spotify Player Section */}
                                        <div>
                                          <h1 className="text-sm font-bold text-white mb-2 flex items-center justify-center gap-2">
                                            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                                              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                            </svg>
                                            Spotify Playlist
                                          </h1>

                                          {/* Spotify Embed Placeholder */}
                                          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-1 mb-3">
                                            <div className="bg-gray-900 rounded-lg h-[120px] flex items-center justify-center">
                                              <div className="text-center">
                                                <svg className="w-16 h-16 text-green-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                                                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                                </svg>
                                                <p className="text-white text-xs">Classical Essentials</p>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Description */}
                                          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2.5 mb-2.5">
                                            <p className="text-white/90 text-xs leading-relaxed">
                                              Diese Playlist wurde sorgfältig zusammengestellt, um das Leseerlebnis der düsteren Gedichte von Edgar Allan Poe zu bereichern.
                                            </p>
                                          </div>

                                          {/* Action Button */}
                                          <button className="w-full bg-green-500 text-white px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 shadow-lg">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                            </svg>
                                            Auf Spotify anhören
                                          </button>
                                        </div>

                                        {/* Footer */}
                                        <div className="mt-3 text-center">
                                          <p className="text-white/60 text-xs">
                                            Powered by Max Mustermann GmbH
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {(hoveredDemo === 'shop' || (!hoveredDemo && lastViewedDemo === 'shop')) && (
                                <div className="w-full h-full overflow-y-auto bg-gray-50">
                                  {/* Multi-Shop Demo Content - Exact copy from ShopDemo.tsx */}
                                  {/* Header with Branding */}
                                  <div className="bg-white shadow-sm">
                                    <div className="px-4 py-3">
                                      <div className="flex items-center justify-center">
                                        <span className="text-sm font-semibold text-blue-600">
                                          Max Mustermann GmbH
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Main Content */}
                                  <div className="px-4 py-4">
                                    {/* Book Info */}
                                    <div className="bg-white rounded-2xl shadow-xl p-3 mb-3">
                                      <div className="flex gap-2 mb-2">
                                        <img 
                                          src="/images/escoffier-und-die-nouvelle-cuisine-1-auflage-pfanneberg.webp" 
                                          alt="Escoffier und die Nouvelle Cuisine"
                                          className="w-20 h-26 rounded-lg shadow-lg object-cover"
                                        />
                                        <div className="flex-1">
                                          <h1 className="text-sm font-bold text-gray-900 mb-0.5">
                                            Escoffier und die Nouvelle Cuisine
                                          </h1>
                                          <p className="text-xs text-gray-600">von Auguste Escoffier</p>
                                          <p className="text-xs text-gray-500 mt-1">ISBN: 978-3-86820-344-7</p>
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-700 leading-relaxed">Das Standardwerk der französischen Haute Cuisine.</p>
                                    </div>

                                    {/* Shop Selection */}
                                    <div className="bg-white rounded-2xl shadow-xl p-3">
                                      <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                                        </svg>
                                        Wählen Sie Ihren Buchhändler
                                      </h2>

                                      {/* Shop List - Condensed */}
                                      <div className="space-y-1.5">
                                        {[
                                          { name: 'Amazon', price: '29.95', available: true },
                                          { name: 'Thalia', price: '29.95', available: true },
                                          { name: 'Buchhandlung am Markt', price: '29.95', available: true, isLocal: true },
                                          { name: 'Hugendubel', price: '28.99', available: true },
                                          { name: 'Weltbild', price: '27.99', available: false }
                                        ].map((shop, index) => (
                                          <div key={index} className="bg-gray-50 rounded-lg p-2">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center">
                                                  {shop.isLocal ? (
                                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                    </svg>
                                                  ) : (
                                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                                    </svg>
                                                  )}
                                                </div>
                                                <div>
                                                  <h3 className="font-semibold text-xs text-gray-900">{shop.name}</h3>
                                                  <p className={`text-xs ${shop.available ? 'text-green-600' : 'text-red-600'}`}>
                                                    {shop.available ? 'Sofort lieferbar' : 'Nicht verfügbar'}
                                                  </p>
                                                </div>
                                              </div>
                                              <span className="text-xs font-bold text-blue-600">
                                                {shop.price} €
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Price Note */}
                                      <div className="mt-3 text-center text-xs text-gray-600">
                                        <p>Preise können variieren.</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {(hoveredDemo === 'author' || (!hoveredDemo && lastViewedDemo === 'author')) && (
                                <div className="w-full h-full overflow-y-auto bg-gray-50">
                                  {/* Author Profile Demo Content - Exact copy from AuthorDemo.tsx */}
                                  {/* Header with Branding */}
                                  <div className="bg-white shadow-sm">
                                    <div className="px-4 py-3">
                                      <div className="flex items-center justify-center">
                                        <span className="text-sm font-semibold" style={{ color: '#4F46E5' }}>
                                          Max Mustermann GmbH
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Main Content */}
                                  <div className="px-4 py-3">
                                    {/* Profile Header */}
                                    <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                      <div className="flex flex-col items-center text-center">
                                        <img
                                          src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Goethe_%28Stieler_1828%29.jpg/440px-Goethe_%28Stieler_1828%29.jpg"
                                          alt="Johann Wolfgang von Goethe"
                                          className="w-20 h-20 rounded-full object-cover shadow-lg mb-3"
                                        />
                                        
                                        <h1 className="text-base font-bold text-gray-900 mb-1">Johann Wolfgang von Goethe</h1>
                                        <p className="text-xs text-gray-600 mb-2">Deutscher Dichter, Naturforscher und Staatsmann</p>
                                        
                                        {/* Social Links */}
                                        <div className="flex gap-1.5">
                                          {[
                                            <svg key="globe" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>,
                                            <svg key="insta" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/></svg>,
                                            <svg key="fb" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
                                            <svg key="x" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                          ].map((icon, index) => (
                                            <div key={index} className="p-1.5 bg-gray-100 rounded-lg">
                                              {icon}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Biography */}
                                    <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                      <h2 className="text-sm font-bold text-gray-900 mb-2">Biografie</h2>
                                      <div className="text-xs text-gray-700 space-y-1.5">
                                        <p>Johann Wolfgang von Goethe (1749-1832) war einer der bedeutendsten deutschen Dichter und Denker.</p>
                                        <p>Seine Werke, darunter "Faust" und "Die Leiden des jungen Werthers", gehören zu den wichtigsten der Weltliteratur.</p>
                                      </div>
                                    </div>

                                    {/* Awards */}
                                    <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                      <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1">
                                        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                        Auszeichnungen
                                      </h2>
                                      <ul className="space-y-1">
                                        <li className="flex items-start gap-1 text-xs">
                                          <span className="text-yellow-500 mt-0.5">•</span>
                                          <span className="text-gray-700">Geadelt durch Kaiser Joseph II. (1782)</span>
                                        </li>
                                        <li className="flex items-start gap-1 text-xs">
                                          <span className="text-yellow-500 mt-0.5">•</span>
                                          <span className="text-gray-700">Mitglied der Leopoldina (1818)</span>
                                        </li>
                                      </ul>
                                    </div>

                                    {/* Selected Works */}
                                    <div className="bg-white rounded-lg shadow-xl p-4">
                                      <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                                        </svg>
                                        Ausgewählte Werke
                                      </h2>
                                      <div className="space-y-1">
                                        {[
                                          { title: 'Faust I & II', year: '1808/1832' },
                                          { title: 'Die Leiden des jungen Werthers', year: '1774' }
                                        ].map((book, index) => (
                                          <div key={index} className="bg-gray-50 p-2 rounded-lg">
                                            <h3 className="font-medium text-xs text-gray-900">{book.title}</h3>
                                            <p className="text-xs text-gray-600">{book.year}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {(hoveredDemo === 'sample' || (!hoveredDemo && lastViewedDemo === 'sample')) && (
                                <div className="w-full h-full overflow-y-auto bg-gradient-to-br from-blue-50 to-indigo-50">
                                  {/* Book Sample Demo Content - Exact copy from SampleDemo.tsx */}
                                  {/* Header */}
                                  <div className="bg-white shadow-sm">
                                    <div className="px-4 py-3">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-semibold text-blue-600">
                                            Max Mustermann GmbH
                                          </span>
                                        </div>
                                        <div className="p-1.5 bg-gray-100 rounded-lg">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Book Info */}
                                  <div className="px-4 py-3">
                                    <div className="bg-white rounded-lg shadow-xl p-3 mb-3">
                                      <div className="flex gap-3 mb-3">
                                        <img
                                          src="/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp"
                                          alt="Der Rabe und andere Gedichte"
                                          className="w-26 h-36 rounded-lg shadow-lg object-cover"
                                        />
                                        
                                        <div className="flex-1">
                                          <h1 className="text-sm font-bold text-gray-900 mb-0.5">Der Rabe und andere Gedichte</h1>
                                          <p className="text-xs text-gray-700 mb-1">von Edgar Allan Poe</p>
                                          
                                          <div className="space-y-0.5 text-xs">
                                            <div>
                                              <span className="text-gray-500">Genre:</span>
                                              <span className="ml-1 font-medium">Lyrik / Horror</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                              </svg>
                                              <span className="font-medium">Max Mustermann Verlag</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="mb-2">
                                        <p className="text-xs text-gray-700 leading-relaxed">Eine Sammlung der berühmtesten Gedichte von Edgar Allan Poe, darunter "Der Rabe".</p>
                                      </div>

                                      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                                        <div className="flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                          </svg>
                                          <span>Erste 3 Kapitel</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                          </svg>
                                          <span>ca. 15 Min.</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Additional Info */}
                                    <div className="bg-white rounded-lg shadow-xl p-3 mb-3">
                                      <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                          <span className="text-gray-500 flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                            Erschienen:
                                          </span>
                                          <p className="font-medium mt-0.5">29.01.1845</p>
                                        </div>
                                        <div>
                                          <span className="text-gray-500 flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                                            </svg>
                                            ISBN:
                                          </span>
                                          <p className="font-medium mt-0.5">978-3-12345-678-9</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* PDF Viewer Preview */}
                                    <div className="bg-white rounded-lg shadow-xl overflow-hidden mb-3">
                                      <div className="bg-gray-100 px-2 py-1.5 flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                          <div className="p-0.5 bg-gray-200 rounded opacity-50">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                                            </svg>
                                          </div>
                                          <span className="text-xs">Seite 1 von ...</span>
                                          <div className="p-0.5 bg-gray-200 rounded">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                            </svg>
                                          </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-1 text-xs">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"/>
                                          </svg>
                                          <span>100%</span>
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/>
                                          </svg>
                                        </div>
                                      </div>
                                      
                                      <div className="relative bg-gray-50" style={{ height: '140px' }}>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="text-center">
                                            <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                            </svg>
                                            <p className="text-gray-500 text-xs">PDF Leseprobe</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Action Button */}
                                    <button className="w-full bg-blue-600 text-white px-3 py-2 rounded-lg font-medium text-xs">
                                      Leseprobe herunterladen
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Phone Details */}
                          <div className="absolute top-0 inset-x-0">
                            <div className="h-[32px] w-[3px] bg-gray-800 absolute left-[-17px] top-[72px] rounded-l-lg"></div>
                            <div className="h-[46px] w-[3px] bg-gray-800 absolute left-[-17px] top-[124px] rounded-l-lg"></div>
                            <div className="h-[46px] w-[3px] bg-gray-800 absolute left-[-17px] top-[178px] rounded-l-lg"></div>
                            <div className="h-[64px] w-[3px] bg-gray-800 absolute right-[-17px] top-[142px] rounded-r-lg"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                </div>
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Progress Indicator */}
      <div className={`fixed left-8 top-1/2 transform -translate-y-1/2 z-50 hidden md:block ${
        isOnHero ? 'opacity-0 pointer-events-none' : 'opacity-100'
      } transition-opacity duration-500`}>
        <div className="w-px h-48 bg-white/25 rounded-full overflow-hidden">
          <div 
            className="w-full bg-white/80 transition-all duration-700 ease-out"
            style={{ height: `${(activeStep / visibleSteps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Navigation Dots (pricing disabled) */}
      <div className={`fixed right-8 top-1/2 transform -translate-y-1/2 z-50 hidden md:block ${
        isOnHero ? 'opacity-0 pointer-events-none' : 'opacity-100'
      } transition-opacity duration-500`}>
        <div className="flex flex-col gap-6">
          {visibleSteps.map((step, index) => (
            <button
              key={index}
              className="flex items-center gap-3 group"
              onClick={() => scrollToSection(index + 1)}
            >
              <span className={`text-xs font-light transition-all duration-500 whitespace-nowrap ${
                activeStep === index
                  ? "text-white/90 opacity-100"
                  : "text-white/60 opacity-60 group-hover:text-white/80 group-hover:opacity-80"
              }`}>
                {step.title}
              </span>
              <div
                className={`w-3 h-3 rounded-full transition-all duration-500 border flex-shrink-0 ${
                  activeStep === index
                    ? "bg-white/90 scale-110 border-white/90"
                    : "bg-transparent border-white/50 group-hover:border-white/80 group-hover:bg-white/20"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gradient-border {
          0%, 100% { border-radius: 37% 29% 27% 27% / 28% 25% 41% 37%; }
          25% { border-radius: 47% 29% 39% 49% / 61% 19% 66% 26%; }
          50% { border-radius: 57% 23% 47% 72% / 63% 17% 66% 33%; }
          75% { border-radius: 28% 49% 29% 100% / 93% 20% 64% 25%; }
        }
        @keyframes gradient-1 {
          0%, 100% { top: 0; right: 0; }
          50% { top: 50%; right: 25%; }
          75% { top: 25%; right: 50%; }
        }
        @keyframes gradient-2 {
          0%, 100% { top: 0; left: 0; }
          60% { top: 75%; left: 25%; }
          85% { top: 50%; left: 50%; }
        }
        @keyframes gradient-3 {
          0%, 100% { bottom: 0; left: 0; }
          40% { bottom: 50%; left: 25%; }
          65% { bottom: 25%; left: 50%; }
        }
        @keyframes gradient-4 {
          0%, 100% { bottom: 0; right: 0; }
          50% { bottom: 25%; right: 40%; }
          90% { bottom: 50%; right: 25%; }
        }
        @keyframes gradient-x {
          0%, 100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }
        .animate-gradient-x {
          animation: gradient-x 4s ease infinite;
        }
        @keyframes blur-in {
          from {
            backdrop-filter: blur(0px);
            -webkit-backdrop-filter: blur(0px);
          }
          to {
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }
        }
        .animate-blur-in {
          animation: blur-in 1s ease-out forwards;
        }
        .backdrop-blur-none {
          backdrop-filter: blur(0px);
          -webkit-backdrop-filter: blur(0px);
        }
      `}</style>

      {/* Footer */}
      <footer className="scroll-section bg-gray-900 text-white py-12" style={{ scrollSnapAlign: 'start', minHeight: 'auto' }}>
        <div className="container mx-auto px-6 w-full">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-0.5 mb-4">
                <Logo size={32} />
                <span className="font-bold text-xl">BeyondPages</span>
              </div>
              <p className="text-gray-400 text-sm">
                Die Plattform für interaktive Bucherlebnisse<br />
                und QR-Code Management.
              </p>
            </div>

            {/* Quick Links (pricing disabled) */}
            <div>
              <h3 className="font-semibold mb-4">Schnelllinks</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/qr/login')} className="text-gray-400 hover:text-white transition-colors">
                    Anmelden
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection(visibleSteps.length)} className="text-gray-400 hover:text-white transition-colors">
                    Demos
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-semibold mb-4">Rechtliches</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => setShowImpressum(true)} className="text-gray-400 hover:text-white transition-colors">
                    Impressum
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white transition-colors">
                    Datenschutz
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white transition-colors">
                    AGB
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold mb-4">Kontakt</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>info@beyondpages.io</li>
                <li>Direkter Support durch unser Team – kurze Wege, klare Ansprechpartner.</li>
              </ul>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>DSGVO-konform</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>SSL-Verschlüsselung</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>AV-Vertrag verfügbar</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>99.9% Uptime</span>
              </div>
            </div>

            <div className="text-center text-sm text-gray-400">
              © {new Date().getFullYear()} BeyondPages. Alle Rechte vorbehalten.
            </div>
          </div>
        </div>
      </footer>

      {/* Impressum Modal */}
      {showImpressum && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowImpressum(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">Impressum</h2>
              <button
                onClick={() => setShowImpressum(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Schließen"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-6">
              <ImpressumContent />
              <div className="mt-8 text-sm text-gray-600">
                <button
                  onClick={() => { setShowImpressum(false); navigate('/impressum'); }}
                  className="underline hover:text-gray-800"
                >
                  Impressum als eigene Seite öffnen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageNew;
